<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcFixedAssetWasteBin',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    fixedAsset: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_donoqg0u4_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_2t7clzgpw_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_fzlrmifv3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MFixedAssetEditor',
      props: {
        target: 'wasteBin',
        fixedAsset: _.cloneDeep(props.fixedAsset),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_awr6mtp72 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i6tqd7irp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_awr6mtp72,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i6tqd7irp],
    event: 'reload',
  });
  const act_2095089031469157_emit_donoqg0u4 = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_donoqg0u4_getActionArgs,
  });
  const inst_evh_2095089031469157_close_2t7clzgpw =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_donoqg0u4,
      event: 'close',
      when: inst_evh_2095089031469157_close_2t7clzgpw_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_fzlrmifv3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_fzlrmifv3_getActionArgs,
      events: [inst_evh_2095089031469157_close_2t7clzgpw],
    });
  const inst_evh_2095089031469157_clickPreventStop_05p6ai1z7 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_fzlrmifv3,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_05p6ai1z7],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_i6tqd7irp,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_2t7clzgpw,
    inst_evh_2095089031469157_clickPreventStop_05p6ai1z7,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_i6tqd7irp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i6tqd7irp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_2t7clzgpw = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_2t7clzgpw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_05p6ai1z7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_05p6ai1z7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('FixedAsset', 'Waste Bin') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAssetWasteBin', 'Type')"
          v-if="fixedAsset.waste_bin.bin_type"
        >
          <span>
            {{
              pgettext('BinTypes', fixedAsset.waste_bin.bin_type, 'StartCase')
            }}
          </span>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAssetWasteBin', 'Number')"
          :content="number(fixedAsset.waste_bin.bin_number)"
          v-if="fixedAsset.waste_bin.bin_number != null"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAssetWasteBin', 'Capacity')"
          v-if="fixedAsset.waste_bin.capacity != null"
        >
          <span>
            {{ fixedAsset.waste_bin.capacity.amount }}
            {{ fixedAsset.waste_bin.capacity.unit }}
          </span>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAssetWasteBin', 'Label')"
        >
          <ItpButton
            :text="pgettext('Button', 'Display label...')"
            :href="$fn.urlFor('render_label', { id: fixedAsset.id })"
            use-auth
            size="sm"
            icon="ri:barcode-box-line"
          >
          </ItpButton>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
